/**
 * Pill — status/role badge used everywhere.
 *
 * Replaces:
 *   - <IndustryStatusBadge> (status pills for industry jobs)
 *   - <JitaContextBadge> (trend pills)
 *   - Tier badges in MiningProfitability ("High-sec" / "Low-sec" / "Null-sec")
 *   - 15+ inline `text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border`
 *     patterns in pages
 *
 * Tones:
 *   - gold    — eve-accent (default brand)
 *   - success — eve-success (green)
 *   - danger  — eve-danger (red)
 *   - warn    — eve-warn (yellow) — was missing from the .pill-* CSS classes
 *   - info    — eve-info (cyan/blue)
 *   - muted   — eve-muted (gray)
 *
 * Sizes:
 *   - xs: 9px text, tight padding (used in tables)
 *   - sm: 10px text (default for most badges)
 *   - md: 11px text, looser padding (used in hero panels)
 *
 * Optional dot — for categorical legends (e.g. tier colors).
 */

import type { ReactNode } from 'react'

export type PillTone = 'gold' | 'success' | 'danger' | 'warn' | 'info' | 'muted'
export type PillSize = 'xs' | 'sm' | 'md'

const TONE_CLASSES: Record<PillTone, string> = {
  gold: 'bg-eve-accent/10 text-eve-accent border-eve-accent/30',
  success: 'bg-eve-success/10 text-eve-success border-eve-success/30',
  danger: 'bg-eve-danger/10 text-eve-danger border-eve-danger/30',
  warn: 'bg-eve-warn/10 text-eve-warn border-eve-warn/30',
  info: 'bg-eve-info/10 text-eve-info border-eve-info/30',
  muted: 'bg-eve-muted/10 text-eve-muted border-eve-border',
}

const SIZE_CLASSES: Record<PillSize, string> = {
  xs: 'text-[9px] px-1 py-0.5',
  sm: 'text-[10px] px-1.5 py-0.5',
  md: 'text-[11px] px-2 py-0.5',
}

export function Pill({
  tone = 'gold',
  size = 'sm',
  dot,
  children,
  className = '',
  title,
}: {
  tone?: PillTone
  size?: PillSize
  /** Show a colored dot before the text (for categorical legends). */
  dot?: boolean
  children: ReactNode
  className?: string
  title?: string
}) {
  const dotClass = {
    gold: 'bg-eve-accent',
    success: 'bg-eve-success',
    danger: 'bg-eve-danger',
    warn: 'bg-eve-warn',
    info: 'bg-eve-info',
    muted: 'bg-eve-muted',
  }[tone]
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border font-mono uppercase font-semibold tracking-wider ${SIZE_CLASSES[size]} ${TONE_CLASSES[tone]} ${className}`.trim()}
      title={title}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />}
      {children}
    </span>
  )
}
